'use client'

import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import { PrismicNextLink } from "@prismicio/next";

/**
 * @typedef {import("@prismicio/client").Content.GuideListingSlice} GuideListingSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<GuideListingSlice>} GuideListingProps
 * @type {import("react").FC<GuideListingProps>}
 */

const GuideCard = ({ item, idx }) => {
  // span mobile/tablette (< xl)
  const baseSpan = idx % 3 === 2 ? "col-span-2" : "col-span-2 md:col-span-1";
  // span desktop (≥ xl)
  const xlSpan = (idx % 5 === 3 || idx % 5 === 4) ? "xl:col-span-3" : "xl:col-span-2";

  return (
    <div className={`${baseSpan} ${xlSpan} h-full`}>
      <PrismicNextLink
        field={item.guide}
        className="h-full flex flex-col gap-6 justify-between no-underline p-5 md:p-7 text-white min-h-70 md:min-h-90 relative bg-cover bg-no-repeat bg-center hover:scale-[1.02] transition-all"
        style={{
          backgroundImage: `linear-gradient(#003A33B2, #003A33B2), url(${item.guide.data.preview_image.url})`,
        }}
      >
        {isFilled.richText(item.guide.data.preview_title) && (
          <PrismicRichText
            field={item.guide.data.preview_title}
            components={{
              heading2: ({ children }) => <h3 className="title-guide">{children}</h3>,
              heading3: ({ children }) => <h3 className="title-guide">{children}</h3>,
            }}
          />
        )}

        {item.guide.tags.length > 0 && (
          <div>
            <span className="text-tags tag-yellow text-dark-green">
              {item.guide.tags[0]}
            </span>
          </div>
        )}
      </PrismicNextLink>
    </div>
  );
};

const GuideListing = ({ slice }) => {
  const { title, text, guides } = slice.primary;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-light-gray py-20 framing text-dark-green"
    >
      <div className="w-full max-w-375 m-auto">
        <div className="flex flex-col gap-12">
          <div className="flex flex-col gap-6">
            {isFilled.richText(title) && (
              <PrismicRichText
                field={title}
                components={{
                  heading2: ({ children }) => <h2 className="h2">{children}</h2>,
                  heading3: ({ children }) => <h3 className="h2">{children}</h3>,
                }}
              />
            )}
            {isFilled.richText(text) && (
              <PrismicRichText
                field={text}
                components={{
                  paragraph: ({ children }) => <p className="max-w-150">{children}</p>,
                }}
              />
            )}
          </div>

          {isFilled.group(guides) && (
            <div className="grid grid-cols-2 xl:grid-cols-6 gap-6 xl:gap-10">
              {guides.map((item, idx) => (
                <GuideCard key={idx} item={item} idx={idx} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section >
  );
};

export default GuideListing;
